import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ArrowRightLeft,
  Package,
  Calendar,
  CheckCircle,
  XCircle,
  Clock,
  Download,
  AlertCircle,
  Eye,
  Filter
} from 'lucide-react'
import api from '../../services/api'

/**
 * Transfers Page
 * صفحة التحويلات - عرض التحويلات الخاصة بالمندوب مع إمكانية الاستلام
 */
const Transfers = () => {
  const navigate = useNavigate()
  const [transfers, setTransfers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [receivingId, setReceivingId] = useState(null)
  const [statusFilter, setStatusFilter] = useState('all')
  const [showFilterMenu, setShowFilterMenu] = useState(false)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [showErrorModal, setShowErrorModal] = useState(false)
  const [selectedTransfer, setSelectedTransfer] = useState(null)
  const [errorMessage, setErrorMessage] = useState('')

  useEffect(() => {
    fetchTransfers()
  }, [statusFilter])

  const fetchTransfers = async () => {
    try {
      setLoading(true)
      setError(null)

      // Build query params with filter
      const params = {}
      if (statusFilter !== 'all') {
        params.status = statusFilter
      }

      const response = await api.get('/mobile/transfers', { params })

      if (response.data.success) {
        setTransfers(response.data.data || [])
      } else {
        setError('فشل في تحميل التحويلات')
      }
    } catch (err) {
      console.error('Error fetching transfers:', err)
      setError(err.response?.data?.message || 'حدث خطأ أثناء تحميل التحويلات')
    } finally {
      setLoading(false)
    }
  }

  const handleReceiveClick = (transfer) => {
    setSelectedTransfer(transfer)
    setShowConfirmModal(true)
  }

  const handleReceiveTransfer = async () => {
    setShowConfirmModal(false)

    try {
      setReceivingId(selectedTransfer.id)
      const response = await api.post(`/mobile/transfers/${selectedTransfer.id}/receive`)

      if (response.data.success) {
        setShowSuccessModal(true)
        await fetchTransfers() // Refresh the list
      } else {
        setErrorMessage(response.data.message || 'فشل في استلام التحويل')
        setShowErrorModal(true)
      }
    } catch (err) {
      console.error('Error receiving transfer:', err)
      setErrorMessage(err.response?.data?.message || 'حدث خطأ أثناء استلام التحويل')
      setShowErrorModal(true)
    } finally {
      setReceivingId(null)
    }
  }

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: {
        label: 'قيد الانتظار',
        color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
        icon: Clock
      },
      approved: {
        label: 'تم الموافقة',
        color: 'bg-green-100 text-green-800 border-green-200',
        icon: CheckCircle
      },
      received: {
        label: 'تم الاستلام',
        color: 'bg-blue-100 text-blue-800 border-blue-200',
        icon: Download
      },
      completed: {
        label: 'تم الاستلام',
        color: 'bg-green-100 text-green-800 border-green-200',
        icon: CheckCircle
      },
      rejected: {
        label: 'مرفوض',
        color: 'bg-red-100 text-red-800 border-red-200',
        icon: XCircle
      },
      completed: {
        label: 'مكتمل',
        color: 'bg-gray-100 text-gray-800 border-gray-200',
        icon: CheckCircle
      }
    }

    const config = statusConfig[status] || statusConfig.pending
    const Icon = config.icon

    return (
      <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${config.color}`}>
        <Icon className="w-3.5 h-3.5" />
        {config.label}
      </div>
    )
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-4 pb-24">
        <div className="max-w-lg mx-auto">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">جاري التحميل...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-4 pb-24">
        <div className="max-w-lg mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
            <p className="text-red-800 font-medium">{error}</p>
            <button
              onClick={fetchTransfers}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              إعادة المحاولة
            </button>
          </div>
        </div>
      </div>
    )
  }

  const getFilterCount = (status) => {
    if (status === 'all') return transfers.length
    return transfers.filter(t => t.status === status).length
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-4 pb-24">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <ArrowRightLeft className="w-7 h-7 text-blue-600" />
                التحويلات
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                عرض وإدارة التحويلات الخاصة بمخزنك
              </p>
            </div>
            {/* Filter Button */}
            <div className="relative">
              <button
                onClick={() => setShowFilterMenu(!showFilterMenu)}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors shadow-sm"
              >
                <Filter className="w-4 h-4 text-gray-600" />
                <span className="text-sm font-medium text-gray-700">فلتر</span>
              </button>

              {/* Filter Menu */}
              {showFilterMenu && (
                <div className="absolute left-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-10">
                  <button
                    onClick={() => {
                      setStatusFilter('all')
                      setShowFilterMenu(false)
                    }}
                    className={`w-full text-right px-4 py-2 hover:bg-gray-50 transition-colors ${
                      statusFilter === 'all' ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'
                    }`}
                  >
                    الكل
                  </button>
                  <button
                    onClick={() => {
                      setStatusFilter('pending')
                      setShowFilterMenu(false)
                    }}
                    className={`w-full text-right px-4 py-2 hover:bg-gray-50 transition-colors ${
                      statusFilter === 'pending' ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'
                    }`}
                  >
                    قيد الانتظار
                  </button>
                  <button
                    onClick={() => {
                      setStatusFilter('received')
                      setShowFilterMenu(false)
                    }}
                    className={`w-full text-right px-4 py-2 hover:bg-gray-50 transition-colors ${
                      statusFilter === 'received' ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'
                    }`}
                  >
                    تم الاستلام
                  </button>
                  <button
                    onClick={() => {
                      setStatusFilter('completed')
                      setShowFilterMenu(false)
                    }}
                    className={`w-full text-right px-4 py-2 hover:bg-gray-50 transition-colors ${
                      statusFilter === 'completed' ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'
                    }`}
                  >
                    مكتمل
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Transfers List */}
        {transfers.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
            <ArrowRightLeft className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600 font-medium">لا توجد تحويلات</p>
            <p className="text-sm text-gray-500 mt-1">سيتم عرض التحويلات هنا عند توفرها</p>
          </div>
        ) : (
          <div className="space-y-4">
            {transfers.map((transfer) => (
              <div
                key={transfer.id}
                className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
              >
                {/* Header */}
                <div className="bg-gradient-to-r from-blue-500 to-indigo-500 px-4 py-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-white">
                      <ArrowRightLeft className="w-5 h-5" />
                      <span className="font-semibold">{transfer.transfer_number}</span>
                    </div>
                    {getStatusBadge(transfer.status)}
                  </div>
                </div>

                {/* Content */}
                <div className="p-4 space-y-3">
                  {/* Warehouse Info */}
                  {transfer.to_warehouse && (
                    <div className="flex items-start gap-2">
                      <Package className="w-4 h-4 text-blue-600 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-xs text-gray-500">المخزن المستهدف</p>
                        <p className="text-sm font-medium text-gray-900">
                          {transfer.to_warehouse.name}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Date */}
                  <div className="flex items-start gap-2">
                    <Calendar className="w-4 h-4 text-gray-400 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-xs text-gray-500">تاريخ التحويل</p>
                      <p className="text-sm font-medium text-gray-900">
                        {new Date(transfer.transfer_date).toLocaleDateString('ar-EG')}
                      </p>
                    </div>
                  </div>

                  {/* Items Count */}
                  <div className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg">
                    <span className="text-sm text-gray-600">عدد الأصناف</span>
                    <span className="text-sm font-semibold text-gray-900">
                      {transfer.items?.length || 0} صنف
                    </span>
                  </div>

                  {/* Notes */}
                  {transfer.notes && (
                    <div className="text-xs text-gray-600 bg-blue-50 p-2 rounded">
                      📝 {transfer.notes}
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex gap-2 pt-2">
                    {/* View Details Button - Always visible */}
                    <button
                      onClick={() => navigate(`/transfers/${transfer.id}`)}
                      className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg font-medium transition-colors bg-white border-2 border-blue-500 text-blue-600 hover:bg-blue-50"
                    >
                      <Eye className="w-4 h-4" />
                      عرض التفاصيل
                    </button>

                    {/* Receive Button - Only for pending transfers */}
                    {transfer.status === 'pending' && (
                      <button
                        onClick={() => handleReceiveClick(transfer)}
                        disabled={receivingId === transfer.id}
                        className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg font-medium transition-colors ${
                          receivingId === transfer.id
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            : 'bg-gradient-to-r from-green-500 to-emerald-500 text-white hover:from-green-600 hover:to-emerald-600 shadow-md hover:shadow-lg'
                        }`}
                      >
                        {receivingId === transfer.id ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            جاري الاستلام...
                          </>
                        ) : (
                          <>
                            <Download className="w-4 h-4" />
                            تم الاستلام
                          </>
                        )}
                      </button>
                    )}
                  </div>

                  {/* Received Info */}
                  {(transfer.status === 'received' || transfer.status === 'completed') && transfer.received_date && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                      <CheckCircle className="w-6 h-6 text-green-600 mx-auto mb-1" />
                      <p className="text-xs text-green-700">
                        تم الاستلام في {new Date(transfer.received_date).toLocaleDateString('ar-EG')}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && selectedTransfer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full animate-scaleIn">
            <div className="bg-gradient-to-r from-blue-500 to-indigo-500 rounded-t-2xl p-6 text-center">
              <div className="bg-white rounded-full w-20 h-20 mx-auto flex items-center justify-center mb-4">
                <Package className="w-10 h-10 text-blue-600" />
              </div>
              <h3 className="text-2xl font-bold text-white">تأكيد الاستلام</h3>
            </div>

            <div className="p-6">
              <p className="text-gray-700 text-lg text-center mb-2">
                هل أنت متأكد من استلام هذا التحويل؟
              </p>
              <p className="text-gray-500 text-sm text-center mb-6">
                رقم التحويل: <span className="font-semibold text-blue-600">{selectedTransfer.transfer_number}</span>
              </p>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowConfirmModal(false)}
                  className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-xl font-semibold hover:bg-gray-300 transition-colors"
                >
                  إلغاء
                </button>
                <button
                  onClick={handleReceiveTransfer}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl font-semibold hover:from-green-600 hover:to-emerald-600 transition-all shadow-lg hover:shadow-xl"
                >
                  تأكيد
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full animate-scaleIn">
            <div className="bg-gradient-to-r from-green-500 to-emerald-500 rounded-t-2xl p-6 text-center">
              <div className="bg-white rounded-full w-20 h-20 mx-auto flex items-center justify-center mb-4">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h3 className="text-2xl font-bold text-white">تم الاستلام بنجاح!</h3>
            </div>

            <div className="p-6">
              <p className="text-gray-700 text-lg text-center mb-6">
                تم استلام التحويل وإضافة الأصناف إلى مخزنك
              </p>

              <button
                onClick={() => setShowSuccessModal(false)}
                className="w-full px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl font-semibold hover:from-green-600 hover:to-emerald-600 transition-all shadow-lg hover:shadow-xl"
              >
                حسناً
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {showErrorModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full animate-scaleIn">
            <div className="bg-gradient-to-r from-red-500 to-pink-500 rounded-t-2xl p-6 text-center">
              <div className="bg-white rounded-full w-20 h-20 mx-auto flex items-center justify-center mb-4">
                <AlertCircle className="w-10 h-10 text-red-600" />
              </div>
              <h3 className="text-2xl font-bold text-white">فشل الاستلام</h3>
            </div>

            <div className="p-6">
              <p className="text-gray-700 text-lg text-center mb-6">
                {errorMessage}
              </p>

              <button
                onClick={() => setShowErrorModal(false)}
                className="w-full px-6 py-3 bg-gradient-to-r from-red-500 to-pink-500 text-white rounded-xl font-semibold hover:from-red-600 hover:to-pink-600 transition-all shadow-lg hover:shadow-xl"
              >
                حسناً
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Transfers
